import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, Upload, Eye, Search, Loader2, CheckCircle, XCircle, 
  Image as ImageIcon, User, X, Camera, Keyboard, Save, Pencil, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocumentsPage() {
  const [searchParams] = useSearchParams();
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processingOcr, setProcessingOcr] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  const [filters, setFilters] = useState({
    client_id: searchParams.get('client_id') || '',
    document_type: ''
  });

  const [uploadData, setUploadData] = useState({
    file: null,
    client_id: searchParams.get('client_id') || '',
    document_type: ''
  });

  const documentTypes = [
    '個人情報誓約書', 'フェースシート', '口座振込同意書', '重要事項説明書',
    '利用契約書', '障害者手帳', '受給者証', '利用計画書', '会議記録',
    'アセスメントシート', '個別支援計画書', '個別支援計画書案', '支援実施書',
    '支援実施書案', 'お薬手帳', '入退所報告書', '個人情報使用同意書', '退職届'
  ];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [docsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/documents`),
        axios.get(`${API}/clients`)
      ]);
      setDocuments(docsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('データの取得に失敗しました', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value === 'all' ? '' : value }));
  };

  const handleUploadChange = (field, value) => {
    setUploadData(prev => ({ ...prev, [field]: value === 'none' ? '' : value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData(prev => ({ ...prev, file }));
      setOcrResult(null);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setPreviewUrl(ev.target.result);
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file) return;
    setUploading(true);
    setOcrResult(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('client_id', uploadData.client_id);
      formData.append('document_type', uploadData.document_type);

      const res = await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.ocr_result) {
        setOcrResult(res.data);
      }
      fetchData();
    } catch (error) {
      console.error('アップロードに失敗しました', error);
      alert('アップロードに失敗しました: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleOcrProcess = async (documentId) => {
    setProcessingOcr(documentId);
    try {
      await axios.post(`${API}/documents/${documentId}/ocr`);
      fetchData();
    } catch (error) {
      alert('OCR処理に失敗しました: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessingOcr(null);
    }
  };

  const handleViewDocument = async (documentId) => {
    try {
      const response = await axios.get(`${API}/documents/${documentId}`);
      setSelectedDocument(response.data);
    } catch (error) {
      console.error('書類の取得に失敗しました', error);
    }
  };

  const resetUpload = () => {
    setUploadData({ file: null, client_id: '', document_type: '' });
    setOcrResult(null);
    setPreviewUrl(null);
  };

  const filteredDocuments = documents.filter(doc => {
    if (filters.client_id && doc.client_id !== filters.client_id) return false;
    if (filters.document_type && doc.document_type !== filters.document_type) return false;
    return true;
  });

  const getOcrStatusBadge = (status) => {
    switch (status) {
      case 'completed': return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800"><CheckCircle size={12} />解析済</span>;
      case 'processing': return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800"><Loader2 size={12} className="animate-spin" />解析中</span>;
      case 'failed': return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800"><XCircle size={12} />失敗</span>;
      default: return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">未処理</span>;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">書類管理</h1>
        <p className="text-muted-foreground mt-1">書類のアップロードとOCR自動解析（Google Cloud Vision API）</p>
      </div>

      {/* アップロード＋OCR結果エリア */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload size={20} className="text-primary" />
            書類アップロード＆自動OCR解析
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 左: アップロード */}
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer relative"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-dropzone"
              >
                {previewUrl ? (
                  <div>
                    <img src={previewUrl} alt="プレビュー" className="max-h-48 mx-auto rounded-lg mb-2" />
                    <p className="text-sm font-medium">{uploadData.file?.name}</p>
                    <p className="text-xs text-muted-foreground">{((uploadData.file?.size || 0) / 1024).toFixed(1)} KB</p>
                  </div>
                ) : uploadData.file ? (
                  <div>
                    <FileText size={40} className="mx-auto text-red-500 mb-2" />
                    <p className="text-sm font-medium">{uploadData.file.name}</p>
                    <p className="text-xs text-muted-foreground">{((uploadData.file.size || 0) / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-center gap-4 mb-3">
                      <Camera size={28} className="text-muted-foreground" />
                      <ImageIcon size={28} className="text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      クリックしてファイルを選択<br />
                      <span className="text-xs">画像・PDFファイル対応（自動OCR解析）</span>
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf,.pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">利用者（任意）</Label>
                  <Select value={uploadData.client_id || 'none'} onValueChange={(v) => handleUploadChange('client_id', v)}>
                    <SelectTrigger data-testid="upload-client-select"><SelectValue placeholder="OCRで自動判定" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">OCRで自動判定</SelectItem>
                      {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">書類種別（任意）</Label>
                  <Select value={uploadData.document_type || 'none'} onValueChange={(v) => handleUploadChange('document_type', v)}>
                    <SelectTrigger data-testid="upload-type-select"><SelectValue placeholder="OCRで自動判定" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">OCRで自動判定</SelectItem>
                      {documentTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-2">
                <Button className="flex-1 btn-active" onClick={handleUpload} disabled={!uploadData.file || uploading} data-testid="upload-button">
                  {uploading ? <><Loader2 size={18} className="mr-2 animate-spin" />OCR解析中...</> : <><Upload size={18} className="mr-2" />アップロード＆OCR解析</>}
                </Button>
                {(uploadData.file || ocrResult) && (
                  <Button variant="outline" onClick={resetUpload} data-testid="reset-upload-button">
                    <X size={18} />
                  </Button>
                )}
              </div>
            </div>

            {/* 右: OCR結果 */}
            <div>
              {uploading ? (
                <div className="flex flex-col items-center justify-center h-full py-12 text-muted-foreground">
                  <Loader2 size={40} className="animate-spin mb-3 text-primary" />
                  <p className="font-medium">Google Cloud Vision で解析中...</p>
                  <p className="text-xs mt-1">高精度日本語OCRで書類を自動認識しています</p>
                </div>
              ) : ocrResult?.ocr_result ? (
                <OcrEditPanel
                  ocrResult={ocrResult}
                  clients={clients}
                  onSave={async (docId, editedData, clientId) => {
                    try {
                      await axios.put(`${API}/documents/${docId}/ocr-data`, {
                        extracted_data: editedData,
                        client_id: clientId,
                      });
                      toast.success('OCRデータを保存しました');
                      fetchData();
                    } catch { toast.error('保存失敗'); }
                  }}
                  onApplyToClient={async (docId, editedData, clientId) => {
                    try {
                      const res = await axios.post(`${API}/documents/${docId}/apply-to-client`, {
                        extracted_data: editedData,
                        client_id: clientId,
                      });
                      toast.success(`利用者データに反映しました（${res.data.updated_fields?.length || 0}項目）`);
                      fetchData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || '反映失敗');
                    }
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-12 text-muted-foreground">
                  <Search size={40} className="mb-3 opacity-30" />
                  <p className="text-sm">書類をアップロードすると</p>
                  <p className="text-sm">自動でOCR解析が実行されます</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 書類一覧 */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle>書類一覧 ({filteredDocuments.length}件)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <Select value={filters.client_id || 'all'} onValueChange={(v) => handleFilterChange('client_id', v)}>
              <SelectTrigger className="w-48" data-testid="filter-client-select"><SelectValue placeholder="利用者で絞り込み" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">すべて</SelectItem>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.document_type || 'all'} onValueChange={(v) => handleFilterChange('document_type', v)}>
              <SelectTrigger className="w-48" data-testid="filter-type-select"><SelectValue placeholder="種別で絞り込み" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">すべて</SelectItem>
                {documentTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {filteredDocuments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText size={48} className="mx-auto mb-2 opacity-50" />
              <p>書類がありません</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDocuments.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors" data-testid={`document-item-${doc.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      {doc.content_type === 'application/pdf' ? <FileText size={20} className="text-red-500" /> : <ImageIcon size={20} className="text-primary" />}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{doc.file_name}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">{doc.document_type || '種別未設定'}</span>
                        {doc.client_name && <span className="text-xs text-muted-foreground flex items-center gap-1"><User size={10} />{doc.client_name}</span>}
                        {getOcrStatusBadge(doc.ocr_status)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0 ml-2">
                    {(doc.ocr_status === 'pending' || doc.ocr_status === 'failed') && (
                      <Button size="sm" variant="outline" onClick={() => handleOcrProcess(doc.id)} disabled={processingOcr === doc.id} data-testid={`ocr-button-${doc.id}`}>
                        {processingOcr === doc.id ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                        <span className="ml-1">OCR解析</span>
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleViewDocument(doc.id)} data-testid={`view-button-${doc.id}`}>
                      <Eye size={14} /><span className="ml-1">表示</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* プレビューモーダル */}
      {selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedDocument(null)}>
          <Card className="card-surface max-w-5xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {selectedDocument.content_type === 'application/pdf' ? <FileText size={20} className="text-red-500" /> : <ImageIcon size={20} className="text-primary" />}
                  {selectedDocument.file_name}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedDocument.document_type || '種別未設定'}
                  {selectedDocument.client_name && ` | ${selectedDocument.client_name}`}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setSelectedDocument(null)} data-testid="close-preview"><X size={20} /></Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium mb-2">ファイルプレビュー</p>
                  {selectedDocument.file_data && selectedDocument.content_type !== 'application/pdf' && (
                    <img src={`data:${selectedDocument.content_type};base64,${selectedDocument.file_data}`} alt={selectedDocument.file_name} className="w-full rounded-lg border border-border" />
                  )}
                  {selectedDocument.content_type === 'application/pdf' && (
                    <div className="flex flex-col items-center justify-center py-12 bg-secondary/50 rounded-lg border border-border">
                      <FileText size={64} className="text-red-400 mb-3" />
                      <p className="text-sm text-muted-foreground">PDFファイル</p>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium mb-2">OCR解析結果</p>
                  {selectedDocument.ocr_result ? (
                    <OcrEditPanel
                      ocrResult={{
                        id: selectedDocument.id,
                        document_id: selectedDocument.id,
                        ocr_result: selectedDocument.ocr_result,
                        document_type: selectedDocument.document_type,
                      }}
                      clients={clients}
                      onSave={async (docId, editedData, clientId) => {
                        try {
                          await axios.put(`${API}/documents/${docId}/ocr-data`, {
                            extracted_data: editedData,
                            client_id: clientId,
                          });
                          toast.success('OCRデータを保存しました');
                          fetchData();
                          // 詳細も更新
                          const updated = await axios.get(`${API}/documents/${docId}`);
                          setSelectedDocument(updated.data);
                        } catch { toast.error('保存失敗'); }
                      }}
                      onApplyToClient={async (docId, editedData, clientId) => {
                        try {
                          const res = await axios.post(`${API}/documents/${docId}/apply-to-client`, {
                            extracted_data: editedData,
                            client_id: clientId,
                          });
                          toast.success(`利用者データに反映しました（${res.data.updated_fields?.length || 0}項目）`);
                          fetchData();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || '反映失敗');
                        }
                      }}
                    />
                  ) : (
                    <div className="text-center py-8 text-muted-foreground bg-secondary/30 rounded-lg">
                      <Search size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">OCR未処理</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}


// OCR結果の編集パネル（プレビュー付き）
function OcrEditPanel({ ocrResult, clients, onSave, onApplyToClient }) {
  const docId = ocrResult?.id || ocrResult?.document_id || '';
  const initialData = ocrResult?.ocr_result?.extracted_data || {};
  const matchedClient = ocrResult?.ocr_result?.matched_client;

  const [editData, setEditData] = useState({ ...initialData });
  const [selectedClientId, setSelectedClientId] = useState(matchedClient?.id || '');
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [newFieldKey, setNewFieldKey] = useState('');

  const updateField = (key, value) => {
    setEditData(prev => ({ ...prev, [key]: value }));
  };

  const removeField = (key) => {
    setEditData(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const addField = () => {
    if (!newFieldKey.trim()) return;
    setEditData(prev => ({ ...prev, [newFieldKey.trim()]: '' }));
    setNewFieldKey('');
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(docId, editData, selectedClientId);
    setSaving(false);
  };

  const handleApply = async () => {
    if (!selectedClientId) {
      toast.error('利用者を選択してください');
      return;
    }
    setApplying(true);
    await onApplyToClient(docId, editData, selectedClientId);
    setApplying(false);
  };

  const confidence = ocrResult?.ocr_result?.confidence || 0;
  const docType = ocrResult?.ocr_result?.document_type || ocrResult?.document_type || '';

  // プレビュー: 受給者証スタイル
  const previewFields = [
    { label: '受給者番号', key: '受給者番号' },
    { label: '氏名', key: '氏名' },
    { label: 'フリガナ', key: 'フリガナ' },
    { label: '生年月日', key: '生年月日' },
    { label: '住所', key: '住所' },
    { label: '電話番号', key: '電話番号' },
    { label: '等級', key: '等級' },
    { label: '支給量', key: '支給量' },
    { label: '負担上限月額', key: '負担上限月額' },
    { label: '有効期間開始', key: '有効期間開始' },
    { label: '有効期間終了', key: '有効期間終了' },
    { label: '交付日', key: '交付日' },
  ];

  return (
    <div className="space-y-3" data-testid="ocr-edit-panel">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle size={16} className="text-emerald-500" />
          <span className="font-semibold text-sm">OCR解析完了</span>
          <span className="text-xs text-muted-foreground">
            信頼度: {Math.round(confidence * 100)}%
          </span>
        </div>
        <div className="flex gap-1">
          <Button variant={showPreview ? 'default' : 'outline'} size="sm" className="h-6 text-[10px]"
            onClick={() => setShowPreview(!showPreview)}>
            <Eye size={12} className="mr-0.5" />{showPreview ? 'プレビュー非表示' : 'プレビュー表示'}
          </Button>
        </div>
      </div>

      {/* 書類種別 & 利用者選択 */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-2 bg-secondary/50 rounded-lg">
          <p className="text-[10px] text-muted-foreground">書類種別</p>
          <p className="text-xs font-medium">{docType}</p>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] text-muted-foreground">利用者</Label>
          <Select value={selectedClientId || 'none'} onValueChange={v => setSelectedClientId(v === 'none' ? '' : v)}>
            <SelectTrigger className="h-7 text-xs" data-testid="ocr-client-select">
              <SelectValue placeholder="利用者を選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">未選択</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* プレビュー（受給者証スタイル） */}
      {showPreview && (
        <div className="border-2 border-gray-300 rounded-lg p-3 bg-white" data-testid="ocr-preview">
          <div className="text-center mb-2">
            <span className="text-xs font-bold border-b border-black pb-0.5">
              {docType || '障害福祉サービス受給者証'}
            </span>
          </div>
          <div className="space-y-0.5">
            {previewFields.map(f => (
              <div key={f.key} className="flex border-b border-gray-200 py-0.5">
                <span className="text-[10px] text-gray-600 w-20 shrink-0">{f.label}</span>
                <span className={`text-[10px] font-medium flex-1 ${editData[f.key] ? 'text-black' : 'text-gray-300'}`}>
                  {editData[f.key] || '未入力'}
                </span>
              </div>
            ))}
            {/* その他のフィールド */}
            {Object.keys(editData).filter(k => !previewFields.some(f => f.key === k)).map(k => (
              <div key={k} className="flex border-b border-gray-200 py-0.5">
                <span className="text-[10px] text-gray-600 w-20 shrink-0">{k}</span>
                <span className="text-[10px] font-medium flex-1">{editData[k]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 編集フォーム */}
      <div className="space-y-1.5" data-testid="ocr-edit-form">
        <div className="flex items-center gap-1 mb-1">
          <Pencil size={12} />
          <span className="text-xs font-semibold">抽出データ編集</span>
        </div>
        {Object.entries(editData).map(([key, value]) => (
          <div key={key} className="flex items-center gap-1.5">
            <Label className="text-[10px] text-muted-foreground w-20 shrink-0 text-right">{key}</Label>
            <Input className="h-7 text-xs flex-1" value={value}
              onChange={e => updateField(key, e.target.value)}
              data-testid={`ocr-field-${key}`} />
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
              onClick={() => removeField(key)}>
              <X size={12} />
            </Button>
          </div>
        ))}

        {/* フィールド追加 */}
        <div className="flex items-center gap-1.5 pt-1 border-t">
          <Input className="h-7 text-xs w-20" placeholder="項目名"
            value={newFieldKey} onChange={e => setNewFieldKey(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addField()} />
          <Button variant="outline" size="sm" className="h-7 text-[10px]" onClick={addField}>
            + 項目追加
          </Button>
        </div>
      </div>

      {/* アクションボタン */}
      <div className="flex gap-2 pt-2 border-t">
        <Button variant="outline" size="sm" className="flex-1" onClick={handleSave} disabled={saving}
          data-testid="ocr-save-btn">
          {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
          編集を保存
        </Button>
        <Button size="sm" className="flex-1" onClick={handleApply} disabled={applying || !selectedClientId}
          data-testid="ocr-apply-btn">
          {applying ? <Loader2 size={14} className="mr-1 animate-spin" /> : <UserCheck size={14} className="mr-1" />}
          利用者データに反映
        </Button>
      </div>
    </div>
  );
}
